/**
 * Embedded document signing providers.
 *
 * The value returned by `POST /cases/:caseId/send_form` in `data.provider`
 *   decides which embed implementation is used. When the backend omits the
 *   field we fall back to HelloSign, which keeps older backends working.
 */
export enum SigningProvider {
  HelloSign = 'hellosign',
  BoldSign = 'boldsign',
}

/**
 * Normalised result of an embedded signing session, so both providers can be
 *   consumed identically by `requestForm$`.
 */
export enum SigningOutcome {
  /** The signer completed the document. */
  Signed = 'signed',
  /**
   * The session ended without a signature — the signer closed the overlay,
   *   declined, or reassigned the document. All of these are user intent, not
   *   errors, so they map to `requestFormCancel`.
   */
  Cancelled = 'cancelled',
}

export const BOLDSIGN_EMBED_ORIGIN =
  import.meta.env.VITE_BOLDSIGN_EMBED_ORIGIN || 'https://app.boldsign.com';

/**
 * `send_form` answers 422 when the provider's template rejects a field. The
 *   body carries a `message` naming the field, which is worth showing verbatim:
 *   it is the likeliest failure while templates are still being migrated.
 */
export const TEMPLATE_FIELD_MISMATCH_STATUS = 422;

/**
 * `send_form` answers 502 when the signing provider refused *us* — a lapsed
 *   plan or a revoked key, carried as `upstream_status`. This is not the
 *   therapist's session or permissions, so it must never log anyone out, and
 *   retrying cannot help until the account is fixed.
 */
export const SIGNING_PROVIDER_UNAVAILABLE_STATUS = 502;

/**
 * Shown for {@link SIGNING_PROVIDER_UNAVAILABLE_STATUS}. Deliberately omits any
 *   suggestion to try again: nothing the therapist does will clear it.
 */
export const SIGNING_UNAVAILABLE_MESSAGE =
  'Document signing is unavailable right now. Please let support know — trying again will not help.';

/**
 * `send_form` answers 409 when an *archived document* already exists for this
 *   case and form type — a `case_files3` row with its upload field populated.
 *   The backend raises it from a guard that predates the BoldSign work and covers
 *   both providers and all three form types.
 *
 * The criterion is the filed document, not a signature: a hand-uploaded intake
 *   trips it with nobody having signed. It is also unbounded in time — an intake
 *   signed at any point in the case's history counts. The constant keeps the
 *   backend's own error name (`Form Already Signed`); the message shown to a
 *   therapist deliberately does not.
 *
 * The body carries `form_type` and `received_at` as separate fields precisely so
 *   the message can be composed here rather than parsed out of a sentence.
 */
export const FORM_ALREADY_SIGNED_STATUS = 409;

/** Fallback for failures the contract does not describe. */
export const REQUEST_FORM_GENERIC_ERROR = 'Something happened requesting form';

/**
 * Client-side events posted by the BoldSign embed iframe.
 * @see https://developers.boldsign.com/embedded-signing/embed-in-an-iFrame/
 */
export enum BoldSignEmbedEvent {
  DocumentSigned = 'onDocumentSigned',
  DocumentSigningFailed = 'onDocumentSigningFailed',
  DocumentReassigned = 'onDocumentReassigned',
  DocumentReassigningFailed = 'onDocumentReassigningFailed',
  DocumentDeclined = 'onDocumentDeclined',
  DocumentDecliningFailed = 'onDocumentDecliningFailed',
}
